import { getDB } from "~/lib/db";
import { routeLoader$ } from "@builder.io/qwik-city";
import { MembershipPlan } from "~/lib/schemas";
import {
  profiles,
  testimonials,
  certifications,
  offers,
  coachingResults,
} from "~/lib/schemas";

const MAX_COACHES = 12;
// eslint-disable-next-line qwik/loader-location
export const useCoaches = routeLoader$(async () => {
  const coaches = await getDB().query.coaches.findMany({
    where: (coaches, { and, isNotNull }) =>
      and(
        isNotNull(coaches.fullName),
        isNotNull(coaches.ghlCalendarId),
        isNotNull(coaches.profilePictureUrl), // Ensure they have a profile picture
      ),
    with: {
      profile: true,
      results: true,
      mainOffer: true,
      certifications: true,
      testimonials: true,
    },
    orderBy: (coaches, { desc, sql }) => [
      // Pro and ProPlus members first
      desc(
        sql`CASE WHEN ${coaches.membershipPlan} = ${MembershipPlan.ProPlus} THEN 3 WHEN ${coaches.membershipPlan} = ${MembershipPlan.Pro} THEN 2 ELSE 1 END`,
      ),
      // Profile quality score based on multiple factors
      desc(
        sql`(
          -- Base profile points (up to 30)
          CASE WHEN COALESCE((SELECT ${sql.raw(profiles.coachingTitle.name)} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${coaches.id} LIMIT 1), '') != '' THEN 10 ELSE 0 END +
          CASE WHEN COALESCE((SELECT ${sql.raw(profiles.title.name)} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${coaches.id} LIMIT 1), '') != '' THEN 10 ELSE 0 END +
          CASE WHEN COALESCE((SELECT ${sql.raw(profiles.description.name)} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${coaches.id} LIMIT 1), '') != '' THEN 10 ELSE 0 END +

          -- Description length points (up to 20)
          CASE
            WHEN LENGTH(COALESCE((SELECT ${sql.raw(profiles.description.name)} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${coaches.id} LIMIT 1), '')) > 500 THEN 20
            WHEN LENGTH(COALESCE((SELECT ${sql.raw(profiles.description.name)} FROM ${profiles} WHERE ${sql.raw(profiles.coachId.name)} = ${coaches.id} LIMIT 1), '')) > 200 THEN 10
            ELSE 0
          END +

          -- Social proof points
          COALESCE((SELECT COUNT(*) * 5 FROM ${testimonials} WHERE ${sql.raw(testimonials.coachId.name)} = ${coaches.id}), 0) +
          COALESCE((SELECT COUNT(*) * 3 FROM ${certifications} WHERE ${sql.raw(certifications.coachId.name)} = ${coaches.id}), 0) +

          -- Offer quality points (up to 30)
          CASE
            WHEN COALESCE((SELECT ${sql.raw(offers.headline.name)} FROM ${offers} WHERE ${offers.id} = ${coaches.mainOfferId} LIMIT 1), '') != '' AND
                 LENGTH(COALESCE((SELECT ${sql.raw(offers.description.name)} FROM ${offers} WHERE ${offers.id} = ${coaches.mainOfferId} LIMIT 1), '')) > 200
              THEN 30
            WHEN COALESCE((SELECT ${sql.raw(offers.headline.name)} FROM ${offers} WHERE ${offers.id} = ${coaches.mainOfferId} LIMIT 1), '') != ''
              THEN 15
            ELSE 0
          END +

          -- Results points
          COALESCE((SELECT COUNT(*) * 4 FROM ${coachingResults} WHERE ${sql.raw(coachingResults.coachId.name)} = ${coaches.id}), 0)
        )`,
      ),
      // Last update time as tiebreaker
      desc(coaches.updatedAt),
    ],
    limit: MAX_COACHES + 1, // Get one extra to check if there are more
  });

  // Calculate profile completeness score for better filtering
  const filteredCoaches = coaches
    .filter((coach) => {
      // Basic profile requirements
      if (
        !coach.profile?.coachingTitle ||
        !coach.profile.title ||
        !coach.profile.description ||
        !coach.mainOffer?.headline
        // || !coach.mainOffer.description
      ) {
        return false;
      }

      // Additional quality checks
      const hasDetailedProfile =
        (coach.profile.description?.length ?? 0) > 100 && // Decent description length
        coach.certifications.length > 0 && // Has certifications
        coach.testimonials.length > 1; // Has multiple testimonials

      return hasDetailedProfile;
    })
    .slice(0, MAX_COACHES);
  //create array from the same item

  return {
    coaches: filteredCoaches,
    /* coaches: Array(5)
      .fill(null)
      .map(() => ({ ...filteredCoaches[0] })), */
  };
});

import { getCoachByUsername } from "~/lib/coach.db";

import type {
  CoachSelect,
  CertificationsSelect,
  TestimonialSelect,
  OfferSelect,
  CoachingResultsSelect,
  OfferTagSelect,
  OfferBenefitSelect,
} from "~/../drizzle/schema";
import type { CoachDetailsSelect, ProfileSelect } from "~/../drizzle/schema";
import type { ResourceSelect } from "~/../drizzle/schema";

type Offer = OfferSelect & {
  tags: OfferTagSelect[];
  benefits: OfferBenefitSelect[];
};
export type CoachData = CoachSelect & {
  profile: ProfileSelect;
  details: CoachDetailsSelect;
  resources: ResourceSelect[];
  certifications: CertificationsSelect[];
  testimonials: TestimonialSelect[];
  results: CoachingResultsSelect[];
  mainOffer: Offer | null;
};
// eslint-disable-next-line qwik/loader-location
export const useCoachData = routeLoader$(async (requestEvent) => {
  const username = requestEvent.params.username;
  const coach = await getCoachByUsername(username);
  if (!coach?.profile) {
    if (!username.includes(".")) {
      throw requestEvent.redirect(302, `/page/${username}`);
    } else {
      throw requestEvent.error(404, "Not found.");
    }
  }

  return coach as CoachData;
});

// eslint-disable-next-line qwik/loader-location
export const useOtherOffers = routeLoader$(async ({ resolveValue }) => {
  const coach = await resolveValue(useCoachData);

  if (coach.membershipPlan !== MembershipPlan.ProPlus || !coach.mainOfferId) {
    return [];
  }

  const offers = await getDB().query.offers.findMany({
    where: (offers, { eq, and, not }) =>
      and(
        eq(offers.coachId, coach.id),
        coach.mainOfferId ? not(eq(offers.id, coach.mainOfferId)) : undefined,
      ),
    columns: {
      id: true,
      headline: true,
      description: true,
    },
    with: {
      tags: {
        columns: {
          id: true,
          title: true,
        },
      },
    },
  });

  return offers;
});
